import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles, LogIn, Zap, Box, Film, Volume2, VolumeX, Play, Mail, Lock, User as UserIcon, Loader2, ArrowLeft } from "lucide-react";

const formatErr = (detail) => {
  if (!detail) return "Something went wrong. Try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
};

export default function SignInOverlay() {
  const { checkAuth } = useAuth();
  const [reels, setReels] = useState([]);
  const [reelIdx, setReelIdx] = useState(0);
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);
  const videoRefB = useRef(null); // secondary layer for crossfade
  const [activeLayer, setActiveLayer] = useState("a"); // 'a' | 'b'

  // 'google' | 'signin' | 'signup' | 'forgot'
  const [tab, setTab] = useState("google");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [forgotSent, setForgotSent] = useState(false);

  // Load a pool of high-energy reels — bias toward action words in the prompt.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/generations?scope=public");
        const all = (data || []).filter((g) => g.type === "story_reel" && g.video_url);
        const ACTION = /\b(warrior|cosmic|energy|battle|explosion|epic|flight|glow|neon|storm|nebula|dynasty|obsidian|solar|thunder|blade|armor|ascend|rise|flame|fire|light)\b/i;
        const actioney = all.filter((r) => ACTION.test(r.prompt || ""));
        const pool = (actioney.length ? actioney : all).slice(0, 8);
        // Shuffle so returning visitors see fresh energy
        for (let i = pool.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [pool[i], pool[j]] = [pool[j], pool[i]];
        }
        setReels(pool);
      } catch { /* silent */ }
    })();
  }, []);

  const reel = reels[reelIdx] || null;
  const reelNext = reels[(reelIdx + 1) % Math.max(1, reels.length)] || null;

  // Crossfade to next reel after 12s of playback for continuous energy
  useEffect(() => {
    if (!reel || reels.length < 2) return;
    const t = setTimeout(() => {
      setActiveLayer((prev) => (prev === "a" ? "b" : "a"));
      setReelIdx((i) => (i + 1) % reels.length);
    }, 12000);
    return () => clearTimeout(t);
  }, [reelIdx, reels.length, reel]);

  // Play both layers muted; the inactive layer preloads the next clip
  useEffect(() => {
    [videoRef, videoRefB].forEach((r) => {
      const v = r.current;
      if (!v) return;
      v.muted = muted;
      v.play().catch(() => {});
    });
  }, [reel, reelNext, muted, activeLayer]);

  const enterGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/studio";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      if (tab === "signup") {
        const referral_code = (() => { try { return localStorage.getItem("geneo_ref") || undefined; } catch { return undefined; } })();
        await api.post("/auth/register", { email, password, name, referral_code });
        toast.success("Welcome! 5 free credits are on your account.");
        try { localStorage.removeItem("geneo_ref"); } catch {}
        await checkAuth();
      } else if (tab === "signin") {
        await api.post("/auth/login", { email, password });
        toast.success("Signed in.");
        await checkAuth();
      } else if (tab === "forgot") {
        await api.post("/auth/forgot-password", { email });
        setForgotSent(true);
      }
    } catch (e2) {
      setErr(formatErr(e2?.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const switchTab = (t) => { setTab(t); setErr(null); setForgotSent(false); };

  const showForm = tab === "signin" || tab === "signup" || tab === "forgot";

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-8 overflow-y-auto"
      data-testid="signin-overlay"
      role="dialog"
      aria-modal="true"
    >
      {/* ─── Full-screen animated backdrop ─────────────────────────── */}
      <div className="absolute inset-0 overflow-hidden" data-testid="signin-backdrop">
        {/* Fallback color while videos load */}
        <div className="absolute inset-0 bg-[#0A0A0A]" />

        {/* Video layer A */}
        {reel && (
          <video
            key={`a-${reel?.generation_id}`}
            ref={videoRef}
            src={activeLayer === "a" ? reel.video_url : reelNext?.video_url || reel.video_url}
            loop playsInline muted autoPlay preload="auto"
            data-testid="signin-backdrop-video"
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1400ms] ${activeLayer === "a" ? "opacity-100" : "opacity-0"}`}
            style={{ animation: "kenBurns 22s ease-in-out infinite alternate" }}
          />
        )}
        {/* Video layer B — crossfade partner */}
        {reelNext && reels.length > 1 && (
          <video
            key={`b-${reelNext?.generation_id}`}
            ref={videoRefB}
            src={activeLayer === "b" ? reel.video_url : reelNext.video_url}
            loop playsInline muted autoPlay preload="auto"
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1400ms] ${activeLayer === "b" ? "opacity-100" : "opacity-0"}`}
            style={{ animation: "kenBurns 22s ease-in-out infinite alternate-reverse" }}
          />
        )}

        {/* Cinematic dark vignette for form legibility — light enough to keep the action visible */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(10,10,10,0.10)_0%,rgba(10,10,10,0.45)_55%,rgba(10,10,10,0.75)_100%)]" />
        {/* Diagonal energy streaks */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#F2C94C]/12 via-transparent to-[#00E5FF]/18 mix-blend-screen pointer-events-none" />
        {/* Ambient orb — gold */}
        <div className="absolute -top-32 -left-32 w-[560px] h-[560px] rounded-full bg-[#F2C94C]/18 blur-[140px] pointer-events-none animate-pulse" style={{ animationDuration: "5s" }} />
        {/* Ambient orb — cyan */}
        <div className="absolute -bottom-32 -right-32 w-[520px] h-[520px] rounded-full bg-[#00E5FF]/20 blur-[140px] pointer-events-none animate-pulse" style={{ animationDuration: "6.5s" }} />
        {/* Top scanline accent */}
        <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#F2C94C]/60 to-transparent" />
        {/* Bottom scanline accent */}
        <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#00E5FF]/50 to-transparent" />
      </div>

      {/* ─── Corner beacon labels ──────────────────────────────────── */}
      <div className="absolute top-6 left-6 hidden md:flex items-center gap-2 pointer-events-none">
        <span className="relative flex w-1.5 h-1.5">
          <span className="absolute inset-0 rounded-full bg-[#F2C94C] animate-ping opacity-70" />
          <span className="relative rounded-full w-1.5 h-1.5 bg-[#F2C94C]" />
        </span>
        <span className="font-mono-hy text-[9px] text-[#F2C94C] tracking-widest">LIVE · DYNASTY REEL</span>
      </div>
      <div className="absolute top-6 right-6 hidden md:flex items-center gap-2 pointer-events-none">
        <span className="font-mono-hy text-[9px] text-[#00E5FF] tracking-widest">GENEO · STUDIO · LOCKED</span>
      </div>

      {/* Mute toggle (hovers over backdrop) */}
      {reel && (
        <button
          onClick={() => setMuted((m) => !m)}
          data-testid="signin-overlay-reel-mute"
          className="absolute bottom-6 right-6 w-10 h-10 rounded-full bg-[#0A0A0A]/70 backdrop-blur border border-[#F2C94C]/25 hover:border-[#F2C94C] hover:bg-[#0A0A0A]/90 flex items-center justify-center transition-all z-10"
          aria-label={muted ? "Unmute backdrop" : "Mute backdrop"}
        >
          {muted ? <VolumeX className="w-4 h-4 text-white" /> : <Volume2 className="w-4 h-4 text-[#F2C94C]" />}
        </button>
      )}

      {/* ─── Centered sign-in card ─────────────────────────────────── */}
      <div className="relative w-full max-w-md rounded-3xl bg-[#120E15]/85 backdrop-blur-xl border border-[#F2C94C]/25 shadow-[0_0_60px_-10px_rgba(242,201,76,0.35)] overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-[#F2C94C] via-[#FF6B4A] to-[#00E5FF]" />

        <div className="p-8 sm:p-10">
            <div className="w-14 h-14 mb-6 rounded-2xl bg-gradient-to-br from-[#F2C94C] to-[#FF6B4A] flex items-center justify-center shadow-[0_0_28px_-6px_rgba(242,201,76,0.7)]">
              <Sparkles className="w-6 h-6 text-[#0A0A0A]" />
            </div>

            <div className="font-mono-hy text-[10px] text-[#F2C94C] mb-2 tracking-wider">GENEO AI · STUDIO · LOCKED</div>
            <h2 className="font-display text-3xl sm:text-4xl text-white leading-tight mb-3">
              {tab === "signup" ? <>Create your account.</> : tab === "forgot" ? <>Reset your password.</> : <>Sign in to start<br />creating.</>}
            </h2>
            <p className="font-body text-sm text-white mb-6">
              {tab === "signup"
                ? <>Get <span className="text-[#F2C94C] font-semibold">5 free credits</span> the moment you sign up. No credit card.</>
                : tab === "forgot"
                ? <>Enter your email and we'll send a link to reset your password.</>
                : <>Afro Sci-Fi animation powered by AI. <span className="text-[#F2C94C] font-semibold">2D, 3D, cinematic</span> — all yours.</>}
            </p>

            {tab === "google" && (
              <>
                {/* Feature bullets — action-packed hooks for Afro 2D + 3D animation */}
                <div className="grid grid-cols-3 gap-2.5 mb-6" data-testid="signin-feature-cards">
                  <div className="rounded-xl border border-[#F2C94C]/25 bg-[#0A0A0A]/60 p-3 hover:border-[#F2C94C]/60 hover:-translate-y-0.5 transition-all" data-testid="signin-feature-create">
                    <Zap className="w-4 h-4 text-[#F2C94C] mb-2" strokeWidth={2.5} />
                    <div className="font-mono-hy text-[9px] text-[#F2C94C] tracking-widest">CREATE</div>
                    <div className="font-body text-[11px] text-white leading-tight mt-0.5">Afro Sci-Fi. 2D &amp; 3D. Unleashed.</div>
                  </div>
                  <div className="rounded-xl border border-[#00E5FF]/30 bg-[#0A0A0A]/60 p-3 hover:border-[#00E5FF]/70 hover:-translate-y-0.5 transition-all" data-testid="signin-feature-3d-worlds">
                    <Box className="w-4 h-4 text-[#00E5FF] mb-2" strokeWidth={2.5} />
                    <div className="font-mono-hy text-[9px] text-[#00E5FF] tracking-widest">3D WORLDS</div>
                    <div className="font-body text-[11px] text-white leading-tight mt-0.5">Characters. Objects. Full dimension.</div>
                  </div>
                  <div className="rounded-xl border border-[#FF6B4A]/35 bg-[#0A0A0A]/60 p-3 hover:border-[#FF6B4A]/70 hover:-translate-y-0.5 transition-all" data-testid="signin-feature-dynasty-reel">
                    <Film className="w-4 h-4 text-[#FF6B4A] mb-2" strokeWidth={2.5} />
                    <div className="font-mono-hy text-[9px] text-[#FF6B4A] tracking-widest">DYNASTY REEL</div>
                    <div className="font-body text-[11px] text-white leading-tight mt-0.5">Animate your vision. Drop-worthy in seconds.</div>
                  </div>
                </div>

                <button
                  onClick={enterGoogle}
                  data-testid="signin-overlay-btn"
                  className="w-full font-mono-hy text-xs px-6 py-4 rounded-2xl bg-[#F2C94C] text-[#0A0A0A] hover:bg-[#F2D06B] transition-all hover:-translate-y-0.5 glow-gold flex items-center justify-center gap-3"
                >
                  <LogIn className="w-4 h-4" /> Sign in with Google
                </button>

                <div className="flex items-center gap-3 my-5">
                  <div className="flex-1 h-px bg-[#F2C94C]/15" />
                  <div className="font-mono-hy text-[9px] text-white tracking-widest">OR</div>
                  <div className="flex-1 h-px bg-[#F2C94C]/15" />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => switchTab("signin")}
                    data-testid="signin-tab-email-login"
                    className="font-mono-hy text-xs px-4 py-3 rounded-2xl border border-[#F2C94C]/30 text-[#F2C94C] hover:bg-[#F2C94C]/10 hover:border-[#F2C94C] transition-all flex items-center justify-center gap-2"
                  >
                    <Mail className="w-3.5 h-3.5" /> Email sign in
                  </button>
                  <button
                    onClick={() => switchTab("signup")}
                    data-testid="signin-tab-email-signup"
                    className="font-mono-hy text-xs px-4 py-3 rounded-2xl border border-[#00E5FF]/30 text-[#00E5FF] hover:bg-[#00E5FF]/10 hover:border-[#00E5FF] transition-all flex items-center justify-center gap-2"
                  >
                    <UserIcon className="w-3.5 h-3.5" /> Create account
                  </button>
                </div>
              </>
            )}
            {showForm && (
              <>
                <button
                  type="button"
                  onClick={() => switchTab("google")}
                  data-testid="signin-back-to-google"
                  className="mb-4 inline-flex items-center gap-1.5 font-mono-hy text-[10px] text-white hover:text-[#F2C94C] transition-colors"
                >
                  <ArrowLeft className="w-3 h-3" /> Back
                </button>

                {tab === "forgot" && forgotSent ? (
                  <div
                    data-testid="forgot-sent-message"
                    className="rounded-2xl border border-[#00E5FF]/30 bg-[#00E5FF]/10 p-5 text-sm text-white"
                  >
                    <div className="font-mono-hy text-[10px] text-[#00E5FF] mb-2">CHECK YOUR EMAIL</div>
                    <p className="font-body text-[#D9D2BE]">
                      If an account exists for <span className="text-white font-semibold">{email}</span>, a reset link is on the way. It expires in 1 hour.
                    </p>
                    <button
                      onClick={() => switchTab("signin")}
                      data-testid="forgot-back-to-signin"
                      className="mt-4 font-mono-hy text-[10px] text-[#F2C94C] hover:underline"
                    >
                      ← Back to sign in
                    </button>
                  </div>
                ) : (
                  <form onSubmit={submit} className="space-y-3" data-testid={`auth-form-${tab}`}>
                    {tab === "signup" && (
                      <div>
                        <label className="font-mono-hy text-[10px] text-white mb-1 block">NAME</label>
                        <div className="relative">
                          <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white" />
                          <input
                            type="text" value={name} onChange={(e) => setName(e.target.value)}
                            data-testid="auth-input-name" placeholder="Nubian Traveller" autoComplete="name"
                            className="w-full pl-10 pr-3 py-3 rounded-xl bg-[#0A0A0A] border border-[#F2C94C]/15 text-white font-body text-sm focus:border-[#F2C94C]/60 focus:outline-none"
                          />
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="font-mono-hy text-[10px] text-white mb-1 block">EMAIL</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white" />
                        <input
                          type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                          data-testid="auth-input-email" placeholder="you@example.com" autoComplete="email" required
                          className="w-full pl-10 pr-3 py-3 rounded-xl bg-[#0A0A0A] border border-[#F2C94C]/15 text-white font-body text-sm focus:border-[#F2C94C]/60 focus:outline-none"
                        />
                      </div>
                    </div>

                    {tab !== "forgot" && (
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="font-mono-hy text-[10px] text-white">PASSWORD</label>
                          {tab === "signin" && (
                            <button type="button" onClick={() => switchTab("forgot")} data-testid="auth-link-forgot"
                                    className="font-mono-hy text-[10px] text-[#F2C94C] hover:underline">
                              Forgot password?
                            </button>
                          )}
                        </div>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white" />
                          <input
                            type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                            data-testid="auth-input-password" placeholder="•••••••••"
                            autoComplete={tab === "signup" ? "new-password" : "current-password"}
                            minLength={tab === "signup" ? 8 : undefined} required
                            className="w-full pl-10 pr-3 py-3 rounded-xl bg-[#0A0A0A] border border-[#F2C94C]/15 text-white font-body text-sm focus:border-[#F2C94C]/60 focus:outline-none"
                          />
                        </div>
                        {tab === "signup" && (
                          <div className="mt-1 font-mono-hy text-[9px] text-white">Minimum 8 characters</div>
                        )}
                      </div>
                    )}

                    {err && (
                      <div data-testid="auth-error" className="rounded-lg border border-[#EB5757]/40 bg-[#EB5757]/10 px-3 py-2 font-body text-xs text-[#EB5757]">
                        {err}
                      </div>
                    )}

                    <button
                      type="submit" disabled={busy}
                      data-testid={`auth-submit-${tab}`}
                      className="w-full font-mono-hy text-xs px-6 py-4 rounded-2xl bg-[#F2C94C] text-[#0A0A0A] hover:bg-[#F2D06B] transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                    >
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      {tab === "signup" ? "Create account" : tab === "forgot" ? "Send reset link" : "Sign in"}
                    </button>

                    <div className="pt-2 text-center">
                      {tab === "signin" && (
                        <button type="button" onClick={() => switchTab("signup")} data-testid="auth-switch-to-signup"
                                className="font-mono-hy text-[10px] text-white hover:text-[#F2C94C]">
                          New to Geneo? <span className="text-[#F2C94C]">Create an account</span>
                        </button>
                      )}
                      {tab === "signup" && (
                        <button type="button" onClick={() => switchTab("signin")} data-testid="auth-switch-to-signin"
                                className="font-mono-hy text-[10px] text-white hover:text-[#F2C94C]">
                          Already have an account? <span className="text-[#F2C94C]">Sign in</span>
                        </button>
                      )}
                      {tab === "forgot" && (
                        <button type="button" onClick={() => switchTab("signin")} data-testid="auth-switch-to-signin"
                                className="font-mono-hy text-[10px] text-white hover:text-[#F2C94C]">
                          Remembered it? <span className="text-[#F2C94C]">Back to sign in</span>
                        </button>
                      )}
                    </div>
                  </form>
                )}
              </>
            )}

            {tab === "google" && (
              <div className="mt-4 font-mono-hy text-[10px] text-white text-center">
                No credit card · 5 free credits on signup
              </div>
            )}
          </div>
        </div>
      </div>
  );
}